'use client';

import { AIModelsResponseSchema } from '@sweetistics/contracts';
import { useCallback, useMemo } from 'react';
import { fromContractModelOption } from '@/lib/ai/model-picker';
import { trpc } from '@/lib/trpc/client';
import type { ModelPickerInitialData, ModelPickerOption } from '@/types/ai';

export type ModelPickerSource = 'chat' | 'available';

interface UseModelPickerOptionsParameters {
  enabled?: boolean;
  initialData?: ModelPickerInitialData | null;
  source: ModelPickerSource;
}

const normalizeModelOptions = (options: readonly ModelPickerOption[]): ModelPickerOption[] =>
  options.map((option) => ({
    ...option,
    description: option.description ?? null,
    config: option.config ?? null,
  }));

interface UseModelPickerOptionsResult {
  defaultId: string | null;
  error: string | null;
  isLoading: boolean;
  metadata: Record<string, unknown>;
  options: ModelPickerOption[];
  reload: () => void;
}

export function useModelPickerOptions({
  source,
  enabled = true,
  initialData,
}: UseModelPickerOptionsParameters): UseModelPickerOptionsResult {
  const fallbackData = useMemo(() => {
    if (!initialData) {
      return null;
    }
    return {
      options: normalizeModelOptions(initialData.options),
      defaultId: initialData.defaultId,
      metadata: initialData.metadata,
    };
  }, [initialData]);

  const modelQuery = trpc.userFeatures.getAIModels.useQuery(undefined, {
    enabled,
    staleTime: 60_000,
    retry: 1,
  });

  const reload = useCallback(() => {
    modelQuery.refetch().catch(() => {
      // ignore refetch errors; query object already exposes error state
    });
  }, [modelQuery]);

  const error = enabled && modelQuery.error instanceof Error ? modelQuery.error.message : null;

  const derivedOptions = useMemo(() => {
    const parsed = modelQuery.data ? AIModelsResponseSchema.safeParse(modelQuery.data) : null;
    if (!parsed?.success) {
      const fallbackOptions = fallbackData?.options;
      return fallbackOptions ? [...fallbackOptions] : [];
    }
    const rawOptions = parsed.data.models.map((model) => fromContractModelOption(model));
    return normalizeModelOptions(rawOptions);
  }, [fallbackData, modelQuery.data]);

  const defaultId = useMemo(() => {
    const parsed = modelQuery.data ? AIModelsResponseSchema.safeParse(modelQuery.data) : null;
    if (!parsed?.success) {
      return fallbackData?.defaultId ?? null;
    }
    return typeof parsed.data.defaultModelId === 'string' ? parsed.data.defaultModelId : null;
  }, [fallbackData, modelQuery.data]);

  const metadata = useMemo<Record<string, unknown>>(() => {
    const parsed = modelQuery.data ? AIModelsResponseSchema.safeParse(modelQuery.data) : null;
    if (!parsed?.success) {
      const fallbackMetadata = fallbackData?.metadata;
      return fallbackMetadata ? { ...fallbackMetadata, source } : { source };
    }
    return {
      success: parsed.data.success,
      isAdmin: parsed.data.isAdmin,
      hasModelSelection: parsed.data.hasModelSelection,
      source,
    };
  }, [fallbackData, modelQuery.data, source]);

  return useMemo(
    () => ({
      options: derivedOptions,
      isLoading: enabled ? modelQuery.isPending : false,
      error,
      defaultId,
      metadata,
      reload,
    }),
    [derivedOptions, enabled, modelQuery.isPending, error, defaultId, metadata, reload]
  );
}
